import { Tooltip } from '@protoss/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useLayout } from '../../contexts/LayoutContext';

export interface NavItemProps {
  /** Menu item identifier */
  id: string;
  /** Icon element (SVG or icon component) */
  icon: React.ReactNode;
  /** Menu label */
  label: string;
  /** Whether this item is currently selected */
  selected?: boolean;
  /**
   * When provided the item renders as a <Link> so Ctrl+Click / middle-click
   * naturally open the target in a new tab. For modifier / non-left clicks the
   * side-effect onClick is intentionally skipped, so the current page stays put
   * while only the new tab navigates.
   */
  href?: string;
  /** Click handler */
  onClick?: () => void;
  /** Keyboard handler */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Visual treatment */
  variant?: 'default' | 'primary' | 'action';
}

/**
 * NavItem - navigation menu item for LeftNavPane
 *
 * Behavior:
 * - Shows icon + label when expanded
 * - Shows only icon with tooltip when collapsed
 * - Supports multiple states: default, hover, selected, focus
 * - Keyboard accessible with Enter/Space activation
 *
 * Design system compliance:
 * - Uses semantic CSS variables only
 * - Follows state styling patterns (data-selected, data-collapsed)
 * - Focus states visible for keyboard navigation
 */
export function NavItem({
  icon,
  label,
  selected = false,
  href,
  onClick,
  onKeyDown,
  variant = 'default',
}: NavItemProps) {
  const { leftNavCollapsed } = useLayout();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
    // Activate on Enter or Space
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  // For the <Link> variant, only run the side-effect onClick on a plain
  // left-click. Ctrl/Cmd/Shift/Alt or middle/right clicks are left to the
  // browser (open in new tab) without mutating the current page - mirrors
  // react-router's own shouldProcessLinkClick guard.
  const handleLinkClick = (e: React.MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    onClick?.();
  };

  const className = `nav-item${variant === 'primary' ? ' nav-item--primary' : variant === 'action' ? ' nav-item--action' : ''}`;
  const sharedProps = {
    className,
    'data-selected': selected || undefined,
    'data-collapsed': leftNavCollapsed || undefined,
    'aria-label': label,
    'aria-current': selected ? ('page' as const) : undefined,
  };

  const inner = (
    <>
      <span className="nav-item-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="nav-item-label">{label}</span>
    </>
  );

  const navItemEl = href ? (
    <Link to={href} {...sharedProps} onClick={handleLinkClick}>
      {inner}
    </Link>
  ) : (
    <button type="button" {...sharedProps} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0}>
      {inner}
    </button>
  );

  // When collapsed, wrap in tooltip
  if (leftNavCollapsed) {
    return (
      <Tooltip.Root delayDuration={300}>
        <Tooltip.Trigger asChild>{navItemEl}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="right" sideOffset={8} className="ui-tooltip">
            {label}
            <Tooltip.Arrow className="ui-tooltip-arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }

  return navItemEl;
}
